import {
  client,
  MinimalEntityDto,
  SitesControllerListSummariesData,
} from "@/client";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormSelectInputItem,
  FormSelectInputItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input";
import {
  FormSelectInputAsyncPaginateQueryItem,
  FormSelectInputAsyncPaginateQueryItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input-async-pagination";
import { useOptionsMapping } from "@/components/custom/form-inputs/select-input/common";
import { cn } from "@/lib/utils";
import { TagsMatchRadioFilter } from "../../data/images/-images-filters";
import { useTags } from "@/hooks/use-tags";
import { useFilterIsActive } from "@/components/custom/data-table/data-table-context";
import { withPermission } from "@/components/custom/with-permission";
import { listPermissions } from "@/lib/list-permissions";

type TForm = SitesControllerListSummariesData;

const SiteIdsFilter = withPermission(
  listPermissions.sites,
  SiteIdsFilterControl,
);
function SiteIdsFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="siteIds"
      label={t("sites")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.sites.listMinimal.queryKey}
      queryCreator={client.sites.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("siteIds")}
      {...props}
    />
  );
}

const TagsFilter = withPermission(listPermissions.siteTags, TagsFilterControl);
function TagsFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const { tagOptions: options } = useTags({
    tagsListQuery: client.sites.listTags
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
  });

  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="tagIds"
      label={t("tags")}
      mapping={mapping as any}
      options={options}
      isMulti={true}
      delimiter={"\n"}
      container={{
        className: cn("md:col-span-2"),
        right: TagsMatchRadioFilter,
      }}
      {...props}
    />
  );
}

export function SitesFilters() {
  return (
    <>
      <SiteIdsFilter />
      <TagsFilter />
    </>
  );
}
